import React, {ReactNode} from 'react';
import {mcInfoFilled, mcLocation} from '@mc/react-ui-icons';
import {Button, Col, Icon, InverseBgProvider, Label, Row, Text} from '@mc/ui-v4';
import removeAccents from 'remove-accents';
import {EnumTypVysledek, LayerVO} from '../../api';
import {getFoundCountLabel, SearchListItem} from './searchPanelTypes';

type Props = {
    item: SearchListItem;
    searchText: string;
    layersConfig?: {[key: string]: LayerVO};
    serverContextPath: string;
    onClick: (item: SearchListItem) => void;
    onMouseEnter: (item: SearchListItem) => void;
    onMouseLeave: () => void;
    onOpenLegend?: () => void;
};

/** Zvýraznění hledaného textu v popisku (shodné se Search.tsx). */
const highlightSearchText = (text: string, searchText: string): ReactNode => {
    if (!text || !searchText) return text;
    const searchPhrases: string[] = removeAccents(searchText)?.toLowerCase().split(' ') || [];
    const splittedText: string[] =
        removeAccents(text)
            ?.toLowerCase()
            .split(new RegExp('(' + searchPhrases.join('|') + ')', 'gi')) || [];
    let start = 0;
    return splittedText.map((part: string, index: number): ReactNode => {
        const end = start + part.length;
        const textPart = text.substring(start, end);
        start = end;
        return searchPhrases.includes(part.toLowerCase()) || part === ' ' ? (
            <span className={'highlight'} key={`${index}-${part}`}>
                {textPart}
            </span>
        ) : (
            textPart
        );
    });
};

const SearchResultRowBase: React.FC<Props> = ({
    item,
    searchText,
    layersConfig,
    serverContextPath,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onOpenLegend,
}) => {
    // Stavový řádek s počtem nalezených objektů (+ volitelně legenda).
    if (item.statusRow) {
        return (
            <Row className={'pl-1 py-0 pr-0 search-status-row align-items-center'} noGutters>
                <Col>
                    <Text italic intent={'secondary'}>
                        {getFoundCountLabel(item.count ?? 0)}
                    </Text>
                </Col>
                {onOpenLegend && (
                    <Col xs={'auto'}>
                        <Button
                            style={'link'}
                            noFocus
                            icon={<Icon intent={'primary'} icon={mcInfoFilled} />}
                            onClick={(e: React.MouseEvent) => {
                                e?.stopPropagation();
                                onOpenLegend();
                            }}
                        />
                    </Col>
                )}
            </Row>
        );
    }

    // Hlavička skupiny (název vrstvy / kategorie výsledků).
    if (item.title) {
        return <Label className={'py-1 px-1'}>{item.title}</Label>;
    }

    const label = item.attrs?.label ?? '';
    const labelSuffix = item.attrs?.labelSuffix;
    const layer = item.attrs?.layer;
    const lc: LayerVO | undefined = layer != null ? layersConfig?.[layer] : undefined;
    const markerImg = lc?.layerImage ?? lc?.style?.marker;
    const nonObjectResult = !item.attrs?.featureId;

    return (
        <div
            className={`search-panel-result-item search-result-row${nonObjectResult ? ' search-result-row--non-object' : ''}${
                item.typVysledek === EnumTypVysledek.LOCATION ? ' search-result-row--location' : ''
            }`}
            onClick={() => onClick(item)}
            onMouseEnter={() => onMouseEnter(item)}
            onMouseLeave={onMouseLeave}
        >
            {/* <div className={'result-icon'}>
                {item.typVysledek === EnumTypVysledek.LOCATION ? (
                    <InverseBgProvider inverse>
                        <div
                            className={'align-items-center d-flex justify-content-center rounded-circle result-location-icon'}
                            style={{backgroundColor: '#294172'}}
                        >
                            <Icon icon={mcLocation} size={'sm'} />
                        </div>
                    </InverseBgProvider>
                ) : markerImg ? (
                    <img className={'result-image layer-icon'} src={`${serverContextPath}/api/files/img/${markerImg}`} alt={lc?.label} />
                ) : (
                    <div className={'result-image-placeholder'} />
                )}
            </div> */}
            <div className={'result-content'}>
                <div className={'result-label'}>{highlightSearchText(label, searchText)}</div>
                {labelSuffix && <div className={'result-suffix'}>{highlightSearchText(labelSuffix, searchText)}</div>}
                {item.resultTypeLabel && (
                    <Text className={'result-record-type'} intent={'secondary'}>
                        {item.resultTypeLabel}
                    </Text>
                )}
            </div>
        </div>
    );
};

/** Zabaleno v React.memo – při re-renderu panelu se řádek nepřekresluje, dokud se nezmění jeho data/handlery. */
export const SearchResultRow = React.memo(SearchResultRowBase);
